<script lang="ts">
    import type { Selected } from 'bits-ui';
    import { t } from 'svelte-i18n';
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { formatCurrency } from '$lib/util/currencyUtil';
    import { number } from 'svelte-i18n';
    import { usePropertiesContext, useStoreContext } from '$lib/context';
    import Button from '$lib/daisyUiComponents/ButtonVV.svelte';
    import { getContext } from 'svelte';
    import { ADD_TO_CART_CONTEXT_KEY, type AddToCartContext } from '$lib/components/productDetails/addToCartContext';
    import { extractVariantOptions } from '$lib/util/productUtil';
    import CustomSelect from '$lib/components/util/CustomSelect.svelte';
    import ProductVariantSelection from '$lib/components/productDetails/ProductVariantSelection.svelte';
    import InfoTabs from '$lib/components/productDetails/InfoTabs.svelte';

    let className = '';
    export { className as class };
    export let vvmLine: string | undefined;

    const properties = usePropertiesContext();
    const storeContext = useStoreContext();

    const addToCartContext = getContext<AddToCartContext>(ADD_TO_CART_CONTEXT_KEY);
    const addToCartMutation = addToCartContext.addToCartMutation;
    const updateCartMutation = addToCartContext.updateCartMutation;

    export let product: ApiSchemas['Product'];
    export let variants: ApiSchemas['Product'][];

    $: beverageCategory = product.translated?.customFields?.custom_lewo_erp_category;

    $: optionGroups = extractVariantOptions(variants, product);

    let quantity: Selected<number> = {
        value: product.calculatedMaxPurchase > 6 ? 6 : product.calculatedMaxPurchase,
        label: (product.calculatedMaxPurchase > 6 ? 6 : product.calculatedMaxPurchase).toString()
    };

    const isAvailable = product.available;

    $: type = product.properties.find((property: { groupId: string }) => property.groupId === properties.type.id);
    $: taste = product.properties.find((property: { groupId: string }) => property.groupId === properties.taste.id);
</script>

<div class="flex h-full justify-between flex-col">
    <div>
        <div class="uppercase flex flex-col gap-2 mb-4">
            <div class="uppercase flex flex-col gap-1.5">
                <div class="flex gap-3 text-sm">
                    {#if beverageCategory !== 'Ticket' && beverageCategory !== 'Paket'}
                        <h2 class="text-primary font-medium tracking-[0.11em]">
                            {type?.translated?.name} | {taste?.translated?.name}
                        </h2>
                    {/if}
                </div>
                <h1 class="product-heading">
                    {product.name ?? product.translated.name}
                    <span class="text-[1.375rem] {vvmLine === 'classic' ? 'font-medium' : 'font-normal'}">
                        {product.translated.customFields.custom_lewo_erp_vdp_specs ?? ''}
                    </span>
                </h1>
            </div>
            <!--
            <div class="flex items-center gap-2 -mt-1 mb-2">
                <ParkerPointsIcon />
                <p class="text-base-300 text-sm normal-case tracking-[0.036rem]">
                    XX/100 {$t('shop.product.parker-points')}
                </p>
            </div>
            -->
        </div>
        {#if product.parentId && beverageCategory !== 'Ticket' && beverageCategory !== 'Paket'}
            <ProductVariantSelection productVariant={product} {variants} class="flex gap-3 mb-8" />
        {:else}
            <div class="flex gap-3 mb-8 md:mb-16 h-16" />
        {/if}
    </div>
    <div class="flex flex-col h-[25rem] gap-8 mt-5">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col">
                <h2 class="text-2xl font-medium">
                    {formatCurrency($number, storeContext, product.calculatedPrice.unitPrice)}
                    {#if product.parentId}
                        <span class="text-sm text-neutral font-normal">
                            {formatCurrency(
                                $number,
                                storeContext,
                                product.calculatedPrice.unitPrice /
                                    parseFloat(
                                        optionGroups?.[properties.size.id]?.options
                                            ?.find(
                                                (option) => option?.id === optionGroups?.[properties.size.id]?.selected
                                            )
                                            ?.name?.replace(/[lL]/g, '')
                                            .replace(',', '.') ?? '0'
                                    )
                            ) + $t('shop.product.per-liter')}
                        </span>
                    {/if}
                </h2>
                <p class="text-sm text-neutral tracking-wide">
                    {$t('shop.product.tax-and-shipping')}
                </p>
            </div>
            <div class="flex gap-3 max-h-12">
                {#if isAvailable}
                    <div class="select-border">
                        <CustomSelect
                            class="select"
                            bind:selected={quantity}
                            options={Array.from(
                                {
                                    length: product.calculatedMaxPurchase
                                },
                                (_, i) => i + 1
                            )}
                        />
                    </div>
                {/if}

                <Button
                    color="primary"
                    class="uppercase {className}"
                    on:click={isAvailable ? () => addToCartContext.addToCart(quantity.value) : undefined}
                    loading={$addToCartMutation.isPending || $updateCartMutation.isPending}
                    disabled={!isAvailable}
                >
                    {#if isAvailable}
                        {$t('shop.product.add-to-cart')}
                    {:else}
                        {$t('shop.product.sold-out')}
                    {/if}
                </Button>
            </div>
            {#if product.calculatedMaxPurchase === product.availableStock && product.calculatedMaxPurchase < 10}
                <span class="text-sm text-error"
                    >{$t('shop.product.low-stock-notification', { values: { amount: product.availableStock } })}</span
                >
            {/if}
        </div>
        <InfoTabs {product} {optionGroups} {beverageCategory} />
    </div>
</div>
